import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import useTranslate from 'application/hooks/use-translate';
import Icon from 'application/components/icon';
import LoadingState from 'application/pages/groups/group/components/reports/loading-state';
import NoDataAvailableState from 'application/pages/groups/group/components/reports/no-data-available-state';
import ReportService from 'application/services/report-service';
import HTMLReport from 'application/pages/groups/group/components/reports/html-report';
import DownloadButton from 'application/components/download-button';
import useToast from 'application/hooks/use-toast';
import downloadFile from 'application/utilities/download-file';
import DatePicker from 'application/components/form/date-picker';

interface Props {
  groupId: string;
}

const ReceivableAccountsReport = ({ groupId }: Props) => {
  const translate = useTranslate('reports');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { data, isFetching, isSuccess } = useQuery({
    queryKey: ['group-statement-report', selectedDate],
    queryFn: () => {
      if (!selectedDate) {
        return Promise.reject();
      }

      return ReportService.fetchReceivableAccountsForPoolReport(groupId, selectedDate);
    },
    enabled: !!selectedDate
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const { addToast } = useToast();
  const fetchFile = () => {
    if (!selectedDate) return;

    setIsDownloading(true);

    return ReportService.downloadReceivableAccountsForPoolReport(groupId, selectedDate)
      .then((response) => {
        if (response.status === 404) {
          addToast({
            message: translate('no-data-available'),
            type: 'warning'
          });

          return;
        }

        if (!response.ok) {
          addToast({
            message: translate('error-downloading-report'),
            type: 'error'
          });

          return;
        }

        downloadFile(response);
      })
      .catch(() => {
        addToast({
          message: translate('error-downloading-report'),
          type: 'error'
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  return (
    <>
      <div className="absolute right-4 top-4 flex items-center space-x-4 print:hidden">
        <DatePicker maxDate={new Date()} onChange={setSelectedDate} value={selectedDate} />
        <DownloadButton disabled={!data || !isSuccess || isFetching} downloading={isDownloading} onClick={fetchFile} />
      </div>

      {isFetching && <LoadingState />}

      {data && isSuccess && !isFetching && <HTMLReport report={data} />}

      {!isFetching && !data && isSuccess && <NoDataAvailableState />}

      {!isFetching && !data && !isSuccess && (
        <div className="flex h-96 flex-col items-center justify-center gap-4 bg-base-200">
          <Icon icon="calendars" className="text-4xl text-gray-400" />

          <span className="w-64 text-center text-gray-400">{translate('select-date')}</span>
        </div>
      )}
    </>
  );
};

export default ReceivableAccountsReport;
